// Presentation catalog for QualityBoost tiers and signals. These are UI-side
// labels/colors for the PHI-free keys the dashboard API delivers — the source of
// truth for the keys themselves is the qualityboost-scoring backend (its
// scoring.Tier constants and signals/catalog signal ids).

// QualityBoostTierMeta is the display metadata for one tier.
export interface QualityBoostTierMeta {
  // key is the backend tier string (scoring.Tier).
  key: string;
  // label is the reviewer-facing name.
  label: string;
  // color is the OQ-CRAWL-29 tier color coding.
  color: string;
  // rank orders the queue: High at the top, Eva Passed at the bottom
  // (mirrors the backend tierRank; higher rank = higher risk).
  rank: number;
}

// QB_TIERS is the ordered tier catalog, highest-risk first — the queue's default
// group order (AC-UI-001-1). Colors per OQ-CRAWL-29 (Heather Slone):
// High = red, Medium = orange, Low = yellow, Eva Passed = green.
export const QB_TIERS: QualityBoostTierMeta[] = [
  { key: "high", label: "High", color: "#d0021b", rank: 3 },
  { key: "medium", label: "Medium", color: "#f5a623", rank: 2 },
  { key: "low", label: "Low", color: "#f8e71c", rank: 1 },
  { key: "passed", label: "Eva Passed", color: "#417505", rank: 0 },
];

const TIER_BY_KEY: { [key: string]: QualityBoostTierMeta } = QB_TIERS.reduce(
  (acc, t) => {
    acc[t.key] = t;
    return acc;
  },
  {} as { [key: string]: QualityBoostTierMeta }
);

// tierMeta resolves a backend tier key to its display metadata, falling back to a
// neutral entry for an unknown key so the UI never renders a blank tier.
export function tierMeta(key: string): QualityBoostTierMeta {
  return (
    TIER_BY_KEY[key] || { key: key, label: key || "Unknown", color: "#9b9b9b", rank: -1 }
  );
}

// QB_SIGNAL_LABELS maps a signal id to its reviewer-facing name. Mirrors the v1
// Layer-A catalog registered in the backend's signals/catalog package
// (REQ-SIG-020). Keep in sync when a signal is added to the backend catalog.
export const QB_SIGNAL_LABELS: { [signalId: string]: string } = {
  "sig-001": "PPS/KPS score not declining (30-day trend)",
  "sig-002": "Weight not declining (30-day trend)",
  "sig-003": "MUAC not declining (30-day trend)",
  "sig-004": "RN comprehensive assessment overdue (14-day)",
  "sig-005": "Missing Plan of Care (POC)",
  "sig-006": "Missing CTI narrative entirely",
  "sig-009": "Diagnosis code not allowed in hospice (primary)",
  "sig-012": "Low ADL dependence (< total assistance)",
  "sig-014": "Uncontrolled pain (>= 7) without intervention",
  "sig-a7": "Shortness of breath without treatment",
};

// signalLabel resolves a signal id to its display name, falling back to the raw
// id so an unmapped signal still renders (rather than disappearing).
export function signalLabel(signalId: string): string {
  return QB_SIGNAL_LABELS[signalId] || signalId;
}



// WEBPACK FOOTER //
// ./src/providers/quality-boost/models/quality-boost-catalog.ts